/**
 * « Par semaine » — le CA encaissé des abonnements, une ligne par semaine,
 * puis le total général.
 *
 * CA TTC d'une semaine = ce que les abonnements ont RAPPORTÉ cette semaine :
 * factures Stripe payées (annuels, mensuels, premières factures comprises)
 * moins les remboursements, à leur date. C'est un encaissé, donc il
 * s'additionne : le total est le CA encaissé de la période. Il diffère de la
 * tuile CA TTC, qui donne le MRR (un chiffre mensuel, pas un cumul).
 *
 * Semaines du lundi au dimanche, coupées aux bornes de la période : la
 * première et la dernière peuvent compter moins de sept jours.
 */

import type { SubscriptionDailyPoint } from '@shared/types/sales';
import type { SubscriptionDeduction } from '@shared/types/settings';

import { formatDayLong, formatMoney } from '../lib/format';
import { Card } from './Card';

interface WeekRow {
  key: string;
  label: string;
  days: number;
  ttc: number;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Lundi de la semaine d'un jour, `YYYY-MM-DD`. */
function mondayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return date.toISOString().slice(0, 10);
}

export function SubscriptionWeeksTable({
  daily,
  deductions,
  vatPercent,
  currency,
}: {
  daily: readonly SubscriptionDailyPoint[];
  deductions: readonly SubscriptionDeduction[];
  vatPercent: number;
  currency: string;
}) {
  const weeks = new Map<string, WeekRow>();
  for (const day of daily) {
    const key = mondayOf(day.date);
    const week = weeks.get(key) ?? { key, label: formatDayLong(day.date), days: 0, ttc: 0 };
    week.days += 1;
    week.ttc += day.netRevenue;
    weeks.set(key, week);
  }
  // La plus récente en haut, le total tout en bas.
  const rows = [...weeks.values()].sort((a, b) => b.key.localeCompare(a.key));

  const money = (value: number) => formatMoney(value, currency);
  const htOf = (ttc: number) => ttc / (1 + vatPercent / 100);
  const deductionOf = (deduction: SubscriptionDeduction, ttc: number) =>
    ((deduction.base === 'ht' ? htOf(ttc) : ttc) * deduction.percent) / 100;
  const finalOf = (ttc: number) => htOf(ttc) - deductions.reduce((sum, d) => sum + deductionOf(d, ttc), 0);

  // Le total part de la somme des CA TTC : les retenues étant des pourcentages,
  // c'est aussi la somme des colonnes (aux arrondis près).
  const totalTtc = rows.reduce((sum, row) => sum + row.ttc, 0);

  const cells = (ttc: number, strong: boolean) => {
    const cell = (value: number, key: string) => (
      <td className="num" key={key}>
        {strong ? <strong>{money(round2(value))}</strong> : money(round2(value))}
      </td>
    );
    return [
      cell(ttc, 'ttc'),
      cell(htOf(ttc), 'ht'),
      ...deductions.map((deduction) => cell(deductionOf(deduction, ttc), `d${deduction.id}`)),
      cell(finalOf(ttc), 'final'),
    ];
  };

  return (
    <Card
      title="Par semaine"
      subtitle="Ce que les abonnements ont rapporté chaque semaine (lundi → dimanche) : factures payées − remboursements"
      className="chart-grid__wide"
    >
      {rows.length === 0 ? (
        <p className="empty">Aucune donnée sur cette période.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Week</th>
                <th className="num">CA TTC</th>
                <th className="num">CA HT</th>
                {deductions.map((deduction) => (
                  <th className="num" key={deduction.id}>
                    {deduction.label}
                  </th>
                ))}
                <th className="num">Final HT</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>
                    {row.label}
                    {row.days < 7 && <span style={{ color: 'var(--text-muted)' }}> ({row.days} j)</span>}
                  </td>
                  {cells(row.ttc, false)}
                </tr>
              ))}
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                {cells(totalTtc, true)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
